'use client';
import Image from 'next/image';
import Link from 'next/link';
import type { Tour } from '@/lib/tourData';

interface TourHeaderProps {
  tour: Tour;
}

export default function TourHeader({ tour }: TourHeaderProps) {
  return (
    <div className="relative w-full overflow-hidden" style={{ minHeight: '340px' }}>
      {/* Background overlay image */}
      <div className="absolute inset-0">
        <Image
          src={tour.bannerImage}
          alt={`${tour.name} cricket tour banner background`}
          fill
          className="object-cover object-top"
          unoptimized
          priority
        />
        {/* Dark gradient overlay */}
        <div className="absolute inset-0 bg-gradient-to-r from-[#07090d] via-[#07090dcc] to-transparent" />
        <div className="absolute inset-0 bg-gradient-to-t from-[#07090d] via-transparent to-[#07090d88]" />
      </div>

      {/* Content */}
      <div className="relative z-10 max-w-[1440px] mx-auto px-4 md:px-8 pt-8 pb-0 flex flex-col justify-end" style={{ minHeight: '340px' }}>
        <div className="flex flex-col gap-3 pb-6 max-w-xl">
          {/* Logo + Title */}
          <div className="flex items-center gap-4">
            <div className="w-16 h-16 md:w-20 md:h-20 rounded-xl overflow-hidden bg-rc-card border border-rc-border flex-shrink-0 shadow-lg">
              <Image
                src={tour.logoImage}
                alt={`${tour.name} logo`}
                width={80}
                height={80}
                className="w-full h-full object-cover"
                unoptimized
              />
            </div>
            <div>
              <h1 className="text-white text-2xl md:text-3xl lg:text-4xl font-bold leading-tight" style={{ fontFamily: 'Saira, sans-serif' }}>
                {tour.name}
              </h1>
            </div>
          </div>

          {/* Meta info */}
          <div className="flex flex-wrap items-center gap-2 text-sm">
            <Link
              href="/cricket"
              className="text-white/85 font-semibold uppercase tracking-wide text-xs px-2 py-0.5 border border-white/20 rounded hover:border-rc-orange transition-colors"
              style={{ fontFamily: 'Saira, sans-serif' }}
            >
              {tour.sport}
            </Link>
            <span className="text-white/40">•</span>
            <span className="text-white/70 text-xs">{tour.format}</span>
            <span className="text-white/40">•</span>
            <span className="text-white/70 text-xs">{tour.startDate} - {tour.endDate}</span>
            <span className="text-white/40">•</span>
            <span className="text-white/70 text-xs">{tour.totalMatches} Matches</span>
          </div>

          {/* CTA Button */}
          <div className="mt-1">
            <Link
              href="/subscribe"
              className="inline-flex items-center gap-2 px-6 py-2.5 bg-rc-orange hover:bg-primary-dark text-white text-sm font-bold uppercase tracking-wider rounded transition-all duration-200 hover:scale-[1.02] active:scale-[0.98]"
            >
              GET A PASS
            </Link>
          </div>
        </div>
      </div>
    </div>
  );
}
